// ============================================
// Canvas Format — Editor visual tipo lienzo
// Convierte entre ChordPro <-> estructura de palabras interactivas
// ============================================

import { parseChordPro, songToChordPro, type ChordProSong, type ChordProLine } from "./chordProParser";

export interface CanvasWord {
  id: string;
  text: string;
  chord: string | null;
}

export type CanvasLineType = "line" | "section" | "empty";

export interface CanvasLine {
  id: string;
  type: CanvasLineType;
  name?: string; // para secciones ("Intro", "Verso", "Coro")
  words: CanvasWord[];
}

export interface CanvasDoc {
  title: string;
  artist: string;
  key: string;
  capo: number;
  tempo: number;
  lines: CanvasLine[];
}

let idCounter = 0;
function nextId(prefix: string): string {
  return `${prefix}_${++idCounter}_${Date.now().toString(36)}`;
}

/** Divide texto en palabras preservando espacios iniciales */
function splitWords(text: string): Array<{ text: string; leadingSpace: boolean }> {
  const words: Array<{ text: string; leadingSpace: boolean }> = [];
  const regex = /(\s*)(\S+)/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    words.push({
      leadingSpace: match[1].length > 0,
      text: match[2],
    });
  }
  return words;
}

/** Convierte ChordProSong → CanvasDoc */
export function chordProToCanvas(song: ChordProSong): CanvasDoc {
  idCounter = 0;
  const lines: CanvasLine[] = [];

  for (const line of song.lines) {
    const canvasLine = chordProLineToCanvas(line);
    if (canvasLine) lines.push(canvasLine);
  }

  return {
    title: song.title,
    artist: song.artist,
    key: song.key || "C",
    capo: song.capo || 0,
    tempo: song.tempo || 120,
    lines,
  };
}

function chordProLineToCanvas(line: ChordProLine): CanvasLine | null {
  if (line.type === "empty") {
    return { id: nextId("line"), type: "empty", words: [] };
  }

  if (line.type === "directive") {
    const text = line.segments[0].text.trim();

    // {comment: Intro} → section
    const commentMatch = text.match(/^\{c(?:omment)?\s*:\s*(.+)}$/i);
    if (commentMatch) {
      return { id: nextId("section"), type: "section", name: commentMatch[1].trim(), words: [] };
    }

    // {soc} → section "Coro"
    if (/^\{(?:soc|start_of_chorus)\}$/i.test(text)) {
      return { id: nextId("section"), type: "section", name: "Coro", words: [] };
    }

    // {sov} → section "Verso"
    if (/^\{(?:sov|start_of_verse)\}$/i.test(text)) {
      return { id: nextId("section"), type: "section", name: "Verso", words: [] };
    }

    // Directivas de cierre u otras → omitir (no representables visualmente)
    if (/^\{(?:eoc|end_of_chorus|eov|end_of_verse)\}$/i.test(text)) {
      return null;
    }

    return null;
  }

  // Línea lírica: convertir segments en palabras
  const words: CanvasWord[] = [];
  let pendingSpace = false;

  for (const seg of line.segments) {
    if (!seg.chord) {
      // Texto plano
      const split = splitWords(seg.text);
      if (split.length === 0) {
        pendingSpace = true;
      } else {
        for (const w of split) {
          words.push({
            id: nextId("word"),
            text: (w.leadingSpace || pendingSpace ? " " : "") + w.text,
            chord: null,
          });
          pendingSpace = false;
        }
      }
    } else {
      // Acorde + texto
      const split = splitWords(seg.text);
      if (split.length === 0) {
        // Texto es solo espacios (ej: \u00A0 entre acordes consecutivos)
        words.push({
          id: nextId("word"),
          text: (pendingSpace ? " " : "") + seg.text,
          chord: seg.chord,
        });
        pendingSpace = false;
      } else {
        for (let i = 0; i < split.length; i++) {
          const w = split[i];
          words.push({
            id: nextId("word"),
            text: (w.leadingSpace || pendingSpace ? " " : "") + w.text,
            chord: i === 0 ? seg.chord : null,
          });
          pendingSpace = false;
        }
      }
    }
  }

  // Filtrar palabras vacías, preservar espacios
  const cleaned = words.filter((w) => w.text.length > 0);

  // Si quedó vacío pero la línea original tenía contenido, crear una palabra vacía
  if (cleaned.length === 0 && line.raw.trim()) {
    cleaned.push({ id: nextId("word"), text: "", chord: null });
  }

  return { id: nextId("line"), type: "line", words: cleaned };
}

/** Convierte CanvasDoc → ChordPro raw string */
export function canvasToChordPro(doc: CanvasDoc): string {
  const lines: string[] = [];
  if (doc.title) lines.push(`{title: ${doc.title}}`);
  if (doc.artist) lines.push(`{artist: ${doc.artist}}`);
  if (doc.key) lines.push(`{key: ${doc.key}}`);
  if (doc.capo) lines.push(`{capo: ${doc.capo}}`);
  if (doc.tempo !== 120) lines.push(`{tempo: ${doc.tempo}}`);
  lines.push("");

  for (const line of doc.lines) {
    if (line.type === "empty") {
      lines.push("");
    } else if (line.type === "section" && line.name) {
      lines.push(`{comment: ${line.name}}`);
    } else {
      let out = "";
      for (const word of line.words) {
        if (word.chord) {
          out += `[${word.chord}]${word.text}`;
        } else {
          out += word.text;
        }
      }
      lines.push(out);
    }
  }

  return lines.join("\n");
}

/** Parsea raw ChordPro → CanvasDoc */
export function parseChordProToCanvas(raw: string): CanvasDoc {
  return chordProToCanvas(parseChordPro(raw));
}

/** Obtiene lista única de acordes usados en el documento */
export function getUsedChords(doc: CanvasDoc): string[] {
  const chords = new Set<string>();
  for (const line of doc.lines) {
    for (const word of line.words) {
      if (word.chord) chords.add(word.chord);
    }
  }
  return Array.from(chords).sort();
}
